import React from "react";

import { valueToString } from "helpers/Helpers";
import classNames from "classnames";

import BaseReportTable from "./BaseReportTable";

import "components/reportTable/reportTable.scss";
import { formatReportCurrency } from "helpers/reportFormat";

interface IProps {
  data: Array<Record<string, any>>;
  columns: Array<Record<string, any>>;
  description: string;
  compileDate: string;
}

export default function DetailedSalesByDepartmentTable(props: IProps) {

  function formatDisplay(type: string, value: string) {
    let newValue = value !== null && value !== undefined ? value : "";

    if (!isNaN(Number(newValue)) && newValue !== "") {
      switch (type) {
        case "currency":
          newValue = formatReportCurrency(value);
          break;
        case "decimal":
          newValue = Number(value).toFixed(2);
          break;
        case "percentage":
          newValue = `${Number(value) * 100}%`;
          break;
        default:
          break;
      }
    } else if (type === "string" && newValue !== "") {
      newValue = valueToString(value).replace(/(^\w|\s\w)/g, c => c.toUpperCase());
    }

    return newValue;
  }

  // Format cell data based on column
  function cellData(columnId: string, cellValue: string, displayType: string, cell: any) {
    switch (columnId) {
      case "title":
        return cell.row?.depth === 3
          ? cell.row?.original?.variant_title !== cell.row?.original?.product_title
            ? formatDisplay(
                cell?.column?.display_type as string,
                `${String(cell.row?.original?.product_title)} - ${String(cell.row?.original?.variant_title)}`,
              )
            : formatDisplay(cell?.column?.display_type as string, cell.row?.original?.variant_title as string)
          : cell.row?.depth === 4
          ? cell.row?.subRows?.length <= 0 && cell.row?.original?.subtitle
            ? formatDisplay(
                cell?.column?.display_type as string,
                `${String(cell.row?.original?.subtitle)} - ${String(cell.row?.original?.variant_title)}`,
              )
            : formatDisplay(cell?.column?.display_type as string, cell.row?.original?.variant_title as string)
          : formatDisplay(cell?.column?.display_type as string, cell.row?.original?.variant_title as string);
      default:
        return formatDisplay(displayType, cellValue);
    }
  }

  function rowClassNames(row: any) {
    return classNames({
      "report-table-row": row.original.type === "product" || !row.original.type,
      "report-table-row-department": row.original.type === "department",
      "report-table-row-category": row.original.type === "category",
      "report-table-row-subcategory": row.original.type === "subcategory",
    });
  }

  function cellClassNames(cell: any, isRowLocked: boolean) {
    return classNames({
      "report-table-row-locked": isRowLocked,
    });
  }

  // Add on click events based on column
  function cellOnClick() {
    return {};
  }

  // Add footer data based on column
  function footerData(columnId: string, footerRow?: Record<string, any>) {
    if (!footerRow) {
      return null;
    }
  
    switch (columnId) {
      case "title":
        return footerRow?.title as string;
  
        case "sales_discount":
        case "sales_subtotal":
        case "sales_tax":
        case "sales_total":
        return formatDisplay(
          "currency",
          String(footerRow[columnId] ?? 0),
        );
      case "sales_quantity":
        return formatDisplay(
          "integer",
          String(footerRow[columnId] ?? 0),
        );
  
      default: {
        const foundColumn = props?.columns?.find(column => column?.id === columnId);
        // Set dynamic tax lines display type to currency
        if (foundColumn) {
          if (foundColumn?.type === "currency") {
            return formatDisplay(
              "currency",
              String(footerRow[columnId] ?? 0),
            );
          }
        }
        return null;
      }
    }
  }

  return (
    <BaseReportTable
      data={props.data}
      columns={props.columns}
      description={props.description}
      compileDate={props.compileDate}
      cellOnClick={cellOnClick}
      cellData={cellData}
      rowClassNames={rowClassNames}
      cellClassNames={cellClassNames}
      footerData={footerData}
      expanding={true}
      expandingColumn="title"
    />
  );
}
